import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { clamp, round } from 'lodash';
import { getOrThrow } from '../../cross-team-shared/misc-utils/strict-record';
import { settingsStoreToken } from '../../cross-team-shared/state/setting-state';
import { shellConnectorToken } from '../../team-apis/core/shell-connector';
import { Point2D } from '../../team-apis/otterdam/answers/shape-typings';
import { CUBE_SIZE } from '../state/cuboid-location-state-3d';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import { buildCuboidPoints3D } from '../utils/cuboid-utils';
import { interpolateLocation } from '../utils/point-cloud-interpolation-helper';
import { buildMatrixWorldFromLocation } from '../utils/point-cloud-utils';
import { reducePoints } from '../utils/point-utils';
import { getProjectionUtils } from '../utils/projections/projection-utils';
import { CalibrationService } from './calibration.service';

@Injectable()
export class ProjectionService extends InjectableSuperclass {
  private shellConnector = inject(shellConnectorToken);
  private settingsStore = inject(settingsStoreToken);
  private store = inject(PointCloudContentStore);
  private calibrationService = inject(CalibrationService);

  getProjectedRectanglePoints(
    cuboidIndex: number,
    videoOutputName: string,
  ): Point2D[] | undefined {
    if (!this.isVideoLoaded(videoOutputName)) {
      return;
    }

    const projectedVertices = this.getProjectedVertices(
      cuboidIndex,
      videoOutputName,
    );

    if (
      !projectedVertices.some(
        (projectedVertex) => projectedVertex.isInsideFrustum,
      )
    ) {
      return;
    }

    return this.buildRectangleVertices(
      projectedVertices.map((vertex) => vertex.position),
      videoOutputName,
    );
  }

  private getProjectedVertices(cuboidIndex: number, videoOutputName: string) {
    const state = this.store.state();
    const vertices = buildCuboidPoints3D({
      position: [0, 0, 0],
      scale: [CUBE_SIZE, CUBE_SIZE, CUBE_SIZE],
    });

    const cuboidState = state.render.cuboids[cuboidIndex];
    const location = interpolateLocation(
      cuboidState.key_locations,
      state.render.frameNumber,
    );
    const modelMatrix = buildMatrixWorldFromLocation(location);

    const projectionUtil = getProjectionUtils(
      this.calibrationService.getProjectionType(videoOutputName),
    );

    const projectedVertices = vertices.map((vertex) =>
      projectionUtil.projectTo2D(vertex, this.settingsStore.state(), state, {
        outputName: videoOutputName,
        modelMatrix,
      }),
    );
    return projectedVertices;
  }

  /**
   * @param projectedVertices Eight vertices of the cuboid projected onto 2D in
   * screen coordinates
   */
  private buildRectangleVertices(
    projectedVertices: Point2D[],
    videoOutputName: string,
  ) {
    const { projectionImageSize } = this.store.state();
    const imageSize = getOrThrow(projectionImageSize, videoOutputName);

    const verticesInImageCoordinates = screenToImageCoordinates(
      projectedVertices,
      imageSize,
    );

    const rectangleVertices = getBoundingBoxPoints(verticesInImageCoordinates);

    if (!hasAtLeastOnePointWithinImage(rectangleVertices, imageSize)) {
      return;
    }

    if (!this.shellConnector.hasFeatureFlag('out-of-bounds-editing')) {
      return clampVerticesToImage(rectangleVertices, imageSize);
    }

    return rectangleVertices;
  }

  private isVideoLoaded(videoOutputName: string) {
    const { projectionImageSize } = this.store.state();
    return isDefined(projectionImageSize[videoOutputName]);
  }
}

function clampVerticesToImage(
  vertices: Point2D[],
  imageSize: Point2D,
): Point2D[] {
  return vertices.map((vertex) => {
    const x = clamp(vertex[0], 0, imageSize[0]);
    const y = clamp(vertex[1], 0, imageSize[1]);
    return [x, y];
  });
}

/**
 * @param projectedCuboidVertices eight vertices of the projected cuboid
 */
function getBoundingBoxPoints(projectedCuboidVertices: Point2D[]): Point2D[] {
  const [maxX, maxY] = reducePoints(projectedCuboidVertices, Math.max).map(
    (coordinate) => round(coordinate, 0),
  );
  const [minX, minY] = reducePoints(projectedCuboidVertices, Math.min).map(
    (coordinate) => round(coordinate, 0),
  );

  // in image coordinates
  return [
    [minX, minY], // top left
    [maxX, minY], // top right
    [maxX, maxY], // bottom right
    [minX, maxY], // bottom left
  ];
}

function screenToImageCoordinates(
  vertices: Point2D[],
  imageSize: Point2D,
): Point2D[] {
  return vertices.map((vertex) => [vertex[0], imageSize[1] - vertex[1]]);
}

function hasAtLeastOnePointWithinImage(
  vertices: Point2D[],
  imageSize: Point2D,
) {
  return vertices.some(
    (vertex) =>
      vertex[0] >= 0 &&
      vertex[0] <= imageSize[0] &&
      vertex[1] >= 0 &&
      vertex[1] <= imageSize[1],
  );
}
